/**
 * module-builder-adapter.ts — interfejs adaptera Kreatora Formularza (hub Kreatora, Krok 2).
 *
 * `ModuleBuilderPage` (canvas + panele) jest od tej pory niezależny od źródła danych:
 * wszystkie zapytania i zapisy idą przez `ModuleBuilderAdapter`. Dwa wcielenia:
 *   - `useTenantModuleBuilderAdapter` (tu) — dotychczasowe zachowanie: moduł custom
 *     tenanta, endpointy `/modules/:id/...`, pełen zestaw operacji (layouty per
 *     kategoria/rola, dodawanie/usuwanie pól),
 *   - adapter szablonu (`lib/module-templates/use-template-form-builder-adapter.ts`) —
 *     hub ITE: dane z definicji szablonu w ADM, zapis przez PATCH widoku `form`
 *     (kontrakt mapowania: `materialize-layouts.ts` Krok 1 + lustro w
 *     `@saas/shared/module-builder-form-layout`). Operacje tenantowe = undefined →
 *     odpowiednie UI (LayoutSwitcher, dodawanie pól, edytory meta) jest ukryte.
 *
 * Typy stanu layoutu (LayoutSection/LayoutElement) przeniesione tu z ModuleBuilderPage —
 * adapter szablonu i strona buildera dzielą jedno źródło kształtu.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ModuleField } from '@/components/fields/FieldRegistry';
import type { LayoutSettings } from '@/components/modules/BuilderSettingsPanel';
import type { LayoutFormValues } from '@/components/modules/LayoutFormModal';
import { CUSTOM_MODULE_PALETTE_SCOPE } from '@/components/modules/palette-scopes';
import type { LayoutElementStyle } from '@/components/modules/style/styleTypes';
import { api } from '@/lib/api';

// ─── Kształt stanu buildera (dawniej w ModuleBuilderPage) ───────────────────

export type ConditionalOperator = 'equals' | 'not_equals' | 'not_empty' | 'empty';

export interface LayoutElement {
  id: string;
  /**
   * Typ elementu. `'field'` → pole modułu (przez `fieldId`). Inny typ (np. `'section'`,
   * `'highlight'`) → layout element z `LayoutElementRegistry` — bez `fieldId`, żyje
   * tylko w `layout_config` JSONB.
   */
  type: string;
  /** Wypełnione TYLKO dla `type === 'field'`. W hubie (szablon) = slug pola. */
  fieldId?: string;
  width: 1 | 2 | 3;
  settings: {
    label?: string | null;
    required?: boolean | null;
    /** Slug pola warunkowego (trigger). Brak = brak warunku. */
    conditionalField?: string | null;
    /** Wartość aktywująca widoczność. */
    conditionalValue?: string | null;
    /** Operator porównania. */
    conditionalOperator?: ConditionalOperator | null;
    /** Per-typ konfiguracja layout elementu (np. tytuł sekcji, treść alertu). */
    [key: string]: unknown;
  };
  style?: LayoutElementStyle;
}

export interface LayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3 | 4 | 5 | 6;
  showHeader: boolean;
  showBorder: boolean;
  collapsible: boolean;
  elements: LayoutElement[];
  /** Zakładka, do której należy sekcja (Krok 8). Brak/nieznane = pierwsza zakładka. */
  tabId?: string | null;
}

/**
 * Zakładka układu formularza (Krok 8) — rejestr w `layout_config.tabs`. Brak klucza
 * `tabs` = stary, płaski układ (jedna niejawna zakładka); dzięki temu odciski
 * istniejących layoutów się nie zmieniają.
 */
export interface FormLayoutTab {
  id: string;
  /** Slug do deep-linków — stabilny przy zmianie tytułu. */
  slug: string;
  title: string;
}

export interface LayoutConfig {
  sections: LayoutSection[];
  tabs?: FormLayoutTab[];
}

export interface CustomModuleLayout {
  id: string;
  moduleId: string;
  layoutType: 'form' | 'list';
  name: string;
  isDefault: boolean;
  layoutConfig: LayoutConfig;
  /** T12.H.11 — przypisanie do kategorii. NULL = uniwersalny. */
  categoryId?: string | null;
  /** T12.H.11 — przypisanie do ról. Puste = dla wszystkich. */
  roleIds?: string[];
}

export interface BuilderModuleDetail {
  id: string;
  slug: string;
  name: string;
  fields: ModuleField[];
  layouts: CustomModuleLayout[];
}

export interface BuilderCategory {
  id: string;
  name: string;
  color: string;
}

export interface BuilderRoleTemplate {
  id: string;
  name: string;
  isSystem: boolean;
}

// ─── Interfejs adaptera ──────────────────────────────────────────────────────

export interface ModuleBuilderAdapter {
  /** 'tenant' = pełny builder modułu custom; 'template' = projektowanie widoku szablonu. */
  mode: 'tenant' | 'template';
  /**
   * Slug modułu/kartoteki przekazywany do `LayoutElementsPalette` do filtrowania klocków
   * scope'owanych (`allowedModules`, np. `attachments_view`, `sale_invoice_audit_view`).
   * Ustawiany przez adapter kartoteki systemowej (= slug klasy dokumentu / kartoteki, np.
   * `sale_invoices`) → paleta pokazuje klocki dokumentowe tak samo jak builder tenanta
   * (`SystemModuleBuilderPage`). Adapter modułu custom tenanta podaje umowny zakres
   * `CUSTOM_MODULE_PALETTE_SCOPE` → paleta pokazuje klocki generyczne + te, które jawnie
   * dopuszczają moduły własne (np. `process_view`). Adapter bez tego pola (szablon huba
   * Kreatora) → `undefined` → paleta pokazuje tylko klocki generyczne, jak dotychczas.
   */
  paletteModuleSlug?: string;
  isLoading: boolean;
  isError: boolean;
  /** Moduł + pola + layouty. W trybie template: jeden syntetyczny layout z formConfig. */
  module: BuilderModuleDetail | undefined;
  categories: BuilderCategory[];
  roleTemplates: BuilderRoleTemplate[] | undefined;
  /** Zapis configu layoutu — jedyny callback zapisu wymagany w obu trybach. */
  saveLayout: (
    layoutId: string,
    config: { sections: LayoutSection[]; tabs?: FormLayoutTab[]; settings?: LayoutSettings },
  ) => Promise<void>;
  /** Komunikat dla błędu zapisu (np. 409 konfliktu wersji w hubie). Brak → generyczny toast. */
  saveErrorMessage?: (err: unknown) => string | undefined;
  /** Odśwież dane po mutacji (invalidacja zapytań adaptera). */
  invalidate: () => void | Promise<void>;
  // Operacje tenantowe — undefined w trybie template → odpowiednie UI ukryte.
  createLayout?: (values: LayoutFormValues, initialConfig: LayoutConfig) => Promise<{ id: string }>;
  updateLayoutMeta?: (layoutId: string, values: LayoutFormValues) => Promise<void>;
  setLayoutDefault?: (layoutId: string) => Promise<void>;
  deleteLayout?: (layoutId: string) => Promise<void>;
  addField?: (input: {
    name: string;
    fieldType: string;
    isRequired: boolean;
  }) => Promise<ModuleField>;
  deleteField?: (fieldId: string) => Promise<void>;
}

// ─── Adapter tenantowy (dotychczasowe zachowanie ModuleBuilderPage) ──────────

interface TenantModuleDetailResponse {
  module: BuilderModuleDetail;
}

/**
 * Adapter modułu custom tenanta — wyniesione 1:1 zapytania i ścieżki zapisu
 * z ModuleBuilderPage. `enabled: false` (builder dostał zewnętrzny adapter,
 * np. szablonu) wyłącza WSZYSTKIE zapytania — w scope admin nie strzela ani
 * moduł, ani kategorie, ani role (`/role-templates` był wcześniej niegated).
 */
export function useTenantModuleBuilderAdapter(
  moduleId: string | undefined,
  options: { enabled?: boolean } = {},
): ModuleBuilderAdapter {
  const enabled = options.enabled ?? true;
  const queryClient = useQueryClient();

  const moduleQuery = useQuery({
    queryKey: ['modules', moduleId],
    queryFn: async () => {
      const res = await api.get<TenantModuleDetailResponse>(`/modules/${moduleId}`);
      return res.data.module;
    },
    enabled: enabled && !!moduleId,
  });

  const categoriesQuery = useQuery({
    queryKey: ['module-categories', moduleId],
    queryFn: async () => {
      const res = await api.get<{ categories: BuilderCategory[] }>(
        `/modules/${moduleId}/categories`,
      );
      return res.data.categories;
    },
    enabled: enabled && !!moduleId,
  });

  // T12.M.A.4 — lista ról tenanta dla LayoutFormModal multiselect + LayoutSwitcher badge.
  // Gated razem z resztą adaptera (hub Krok 2): wcześniej strzelała przy każdym mount.
  const roleTemplatesQuery = useQuery({
    queryKey: ['role-templates'],
    queryFn: async () => {
      const res = await api.get<{ roleTemplates: BuilderRoleTemplate[] }>('/role-templates');
      return res.data.roleTemplates;
    },
    enabled: enabled && !!moduleId,
  });

  return {
    mode: 'tenant',
    // Moduł własny: paleta dopuszcza klocki z `CUSTOM_MODULE_PALETTE_SCOPE` w `allowedModules`.
    paletteModuleSlug: CUSTOM_MODULE_PALETTE_SCOPE,
    isLoading: moduleQuery.isLoading,
    isError: moduleQuery.isError,
    module: moduleQuery.data,
    categories: categoriesQuery.data ?? [],
    roleTemplates: roleTemplatesQuery.data,
    saveLayout: async (layoutId, config) => {
      await api.put(`/modules/${moduleId}/layouts/${layoutId}`, {
        layoutConfig: {
          sections: config.sections,
          // Klucz `tabs` tylko gdy zakładki istnieją — brak = stary, płaski układ.
          ...(config.tabs && config.tabs.length > 0 ? { tabs: config.tabs } : {}),
          settings: config.settings,
        },
      });
    },
    invalidate: () =>
      // `module-by-slug` zasila formularz/listę rekordów (staleTime 60 s) — bez unieważnienia
      // nowo dodane pole/layout nie pojawia się na formatce „Nowy rekord".
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['modules', moduleId] }),
        queryClient.invalidateQueries({ queryKey: ['module-by-slug'] }),
      ]).then(() => undefined),
    createLayout: async (values, initialConfig) => {
      const res = await api.post<{ layout: { id: string } }>(`/modules/${moduleId}/layouts`, {
        layoutType: 'form',
        name: values.name,
        isDefault: values.isDefault,
        layoutConfig: initialConfig,
        categoryId: values.categoryId,
        roleIds: values.roleIds, // T12.M.A.4 — propagacja wybranych ról
      });
      return res.data.layout;
    },
    updateLayoutMeta: async (layoutId, values) => {
      await api.put(`/modules/${moduleId}/layouts/${layoutId}`, {
        name: values.name,
        isDefault: values.isDefault,
        categoryId: values.categoryId,
        roleIds: values.roleIds,
      });
    },
    setLayoutDefault: async (layoutId) => {
      await api.put(`/modules/${moduleId}/layouts/${layoutId}`, { isDefault: true });
    },
    deleteLayout: async (layoutId) => {
      await api.delete(`/modules/${moduleId}/layouts/${layoutId}`);
    },
    addField: async (input) => {
      const res = await api.post<{ field: ModuleField }>(`/modules/${moduleId}/fields`, input);
      return res.data.field;
    },
    deleteField: async (fieldId) => {
      await api.delete(`/modules/${moduleId}/fields/${fieldId}`);
    },
  };
}
